// CARO. Casa de estilo — COLORIMETRY ME!: the result carried in a link (`?ficha=`), so a person
// stuck in an app's browser (Facebook, Instagram…: no downloads there) can open the same result in
// Chrome or Safari and download the ficha. Only what the game needs travels: the answers, the
// hairstyle, the score, the season shown and the colour picked. Never the alias. The page reads it
// once and removes it from the address bar.
import type { Answers } from './engine';
import type { Question, Season } from './schema';

export const HANDOFF_PARAM = 'ficha';

const HAIR = ['short', 'bob', 'long', 'afro'] as const;
export type HandoffHair = (typeof HAIR)[number];

export interface Handoff {
  answers: Record<string, string>;
  hair: HandoffHair;
  /** «Dominas tu paleta» out of 10, when both games were played. */
  score: number | null;
  /** The season on screen (the primary or the alternative), if one was picked. */
  season?: string;
  /** The colour the croquis wears. */
  top?: string;
}

const toBase64Url = (s: string) => btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromBase64Url = (s: string) => atob(s.replace(/-/g, '+').replace(/_/g, '/'));

export function encodeHandoff(h: Omit<Handoff, 'answers'> & { answers: Answers }): string {
  return toBase64Url(JSON.stringify({ a: h.answers, h: h.hair, s: h.score, v: h.season, t: h.top }));
}

/**
 * The result in `raw`, or null when it isn't one. Anything unknown (a question or option that no
 * longer exists, a bad colour) is dropped rather than trusted: the link may be old or edited by hand.
 */
export function decodeHandoff(
  raw: string,
  data: { questions: readonly Question[]; seasons: readonly Season[] },
): Handoff | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(fromBase64Url(raw));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const { a, h, s, v, t } = parsed as Record<string, unknown>;
  if (!a || typeof a !== 'object') return null;
  const answers: Record<string, string> = {};
  for (const q of data.questions) {
    const option = (a as Record<string, unknown>)[q.id];
    if (typeof option === 'string' && q.options.some((o) => o.id === option)) answers[q.id] = option;
  }
  if (!Object.keys(answers).length) return null;
  return {
    answers,
    hair: HAIR.includes(h as HandoffHair) ? (h as HandoffHair) : 'bob',
    score: typeof s === 'number' && Number.isInteger(s) && s >= 0 && s <= 10 ? s : null,
    season: typeof v === 'string' && data.seasons.some((x) => x.id === v) ? v : undefined,
    top: typeof t === 'string' && /^#[0-9a-f]{6}$/i.test(t) ? t.toLowerCase() : undefined,
  };
}
